import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, RotateCw, Upload, ArrowRightLeft, X, Package, FileText, Loader2 } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { DateRangePicker, DatePreset, rangeForPreset } from '@/components/ui/DateRangePicker';
import { Modal } from '@/components/ui/Modal';

/**
 * One row per shipment LEG. A parcel that went out and came back has TWO rows against the same
 * AWB/LR: `FORWARD` (freight + COD charge) and `RTO` (return freight, with a NEGATIVE codCharge
 * reversing the COD fee — no COD is collected on a return).
 */
/** WEIGHT, WEIGHT-2, … are all "the extra charged after invoicing", so they render identically. */
const isWeightLeg = (t?: string) => !!t && t.startsWith('WEIGHT');

type FreightRow = {
  id: number;
  legType: string;                 // FORWARD | RTO | WEIGHT | WEIGHT-2 …
  orderId?: string;
  orderType: 'B2C' | 'B2B';
  orderNo: string;
  awbNo?: string;
  lrNo?: string;
  bookingDate: string;
  merchant?: { id: number; companyName: string; brandName?: string };
  courier?: { id: number; name: string; addressLabelName?: string };
  chargedWeight: number;
  /** On a WEIGHT row this is the weight the invoiced row was billed at, so the row reads X → Y kg. */
  bookedWeight?: number | null;
  zone: string;
  codCharge: number;
  subtotal: number;
  forwardCharge: number;
  rtoCharge: number;
  gst: number;
  total: number;
  createdAt: string;
  chargedAt: string;
};

type FreightBucket = { shipments: number; rtoLegs?: number; subtotal: number; gst: number; total: number; cod: number; fuel: number };
type FreightSummary = {
  basis: string; gstPct: number;
  today: FreightBucket; yesterday: FreightBucket; thisMonth: FreightBucket;
  filtered: FreightBucket & { from: string | null; to: string | null };
};

/**
 * Two ways to write money on this screen, and they are not interchangeable.
 *
 * `amt` — a CHARGE, to the paise: base, additional, COD, fuel, each B2B surcharge, the subtotal they
 *         add up to and the GST on it. This is the arithmetic a merchant checks a rate card against,
 *         and ₹4.50/kg on 12.5 kg is ₹56.25.
 *
 * `amt0` — a TOTAL, to the rupee: the row's Total, the summary tiles, the shipment total. It is what
 *          gets paid and what the invoice carries, and the backend now stores it that way too, so
 *          printing `.00` on it would only suggest a precision that is not there.
 */
const amt = (n: number | null | undefined) => Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const amt0 = (n: number | null | undefined) => Math.round(Number(n ?? 0)).toLocaleString('en-IN');
/** ₹ + a charge, to the paise. */
const inr = (n: number) => `₹${amt(n)}`;
/** ₹ + a total, to the rupee. */
const inr0 = (n: number) => `₹${amt0(n)}`;

/** One tile of the internal-ops freight summary strip. */
function SummaryTile({ label, sub, b, accent }: { label: string; sub?: string; b?: FreightBucket; accent?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        {/* Shipments counts FORWARD legs only — an RTO row is a second charge on a parcel already
            counted, not a second parcel. */}
        <div className="text-[11px] text-slate-400">
          {(b?.shipments ?? 0).toLocaleString('en-IN')} shipments
          {(b?.rtoLegs ?? 0) > 0 && <span className="text-amber-600"> · {b?.rtoLegs} RTO</span>}
        </div>
      </div>
      <div className="text-xl font-semibold text-slate-900 mt-1">{inr0(b?.total ?? 0)}</div>
      <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
        <div>Freight <span className="text-slate-700 font-medium">{inr(b?.subtotal ?? 0)}</span></div>
        <div>GST <span className="text-slate-700 font-medium">{inr(b?.gst ?? 0)}</span> · COD <span className="text-slate-700 font-medium">{inr(b?.cod ?? 0)}</span></div>
      </div>
      {sub && <div className="text-[10px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function FreightPage({ orderType }: { orderType: 'B2C' | 'B2B' }) {
  const qc = useQueryClient();
  // One search box for AWB / LR / Order No — the server matches all three (`q`). Debounced so
  // typing an order number doesn't fire a request per keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [awbSearch, setAwbSearch] = useState('');
  const [merchantIds, setMerchantIds] = useState<number[]>([]);
  const { user: _au } = useAuth();
  const isMerchant = _au?.kind === 'merchant';
  const [courierIds, setCourierIds] = useState<number[]>([]);
  // Default the Freight Date filter to the current month (was: today).
  const _thisMonth = rangeForPreset('this-month');
  const [dateFrom, setDateFrom] = useState(_thisMonth.startDate);
  const [dateTo, setDateTo] = useState(_thisMonth.endDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('this-month');
  const [bookingFrom, setBookingFrom] = useState('');
  const [bookingTo, setBookingTo] = useState('');
  const [bookingPreset, setBookingPreset] = useState<DatePreset>('custom');
  const [legType, setLegType] = useState<'' | 'FORWARD' | 'RTO' | 'WEIGHT'>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [showRecalc, setShowRecalc] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [orderModalId, setOrderModalId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setAwbSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Freight Date defaults to the current month, so searching for an older AWB/order would otherwise
  // come back empty and read as "not found". A search is a direct lookup: while one is active the
  // date range is dropped (and the UI says so) instead of silently hiding the match.
  const searching = awbSearch.length > 0;

  const params = useMemo(() => {
    const p: any = { orderType, page, pageSize };
    if (awbSearch) p.q = awbSearch;
    if (legType) p.legType = legType;
    if (merchantIds.length) p.merchantIds = merchantIds.join(',');
    if (courierIds.length) p.courierIds = courierIds.join(',');
    if (!searching) {
      if (dateFrom) p.fromDate = dateFrom;
      if (dateTo) p.toDate = dateTo;
    }
    // The Order Booking range has to go too. It was left applied while searching, so pasting an AWB
    // from outside the chosen booking window still returned nothing — the same "not found" the
    // charge-date range was already fixed for.
    if (!searching) {
      if (bookingFrom) p.orderBookingFrom = bookingFrom;
      if (bookingTo) p.orderBookingTo = bookingTo;
    }
    return p;
  }, [orderType, page, pageSize, awbSearch, searching, legType, merchantIds, courierIds, dateFrom, dateTo, bookingFrom, bookingTo]);

  const { data, isLoading } = useQuery({
    queryKey: ['freight', params],
    queryFn: async () => (await api.get<{ total: number; items: FreightRow[] }>('/freight', { params })).data,
  });

  // Charged / GST totals for the ops strip. Internal staff only — the route is @Roles(INTERNAL_STAFF)
  // and merchants must not see platform-wide revenue, so the query isn't even issued for them.
  // Shares the merchant/courier/date filters with the list so the tiles and the rows agree.
  const summaryParams = useMemo(() => {
    const p: any = { orderType };
    if (awbSearch) p.q = awbSearch;
    if (legType) p.legType = legType;
    if (merchantIds.length) p.merchantIds = merchantIds.join(',');
    if (courierIds.length) p.courierIds = courierIds.join(',');
    if (!searching) {
      if (dateFrom) p.fromDate = dateFrom;
      if (dateTo) p.toDate = dateTo;
    }
    return p;
  }, [orderType, awbSearch, searching, legType, merchantIds, courierIds, dateFrom, dateTo]);

  const { data: summary } = useQuery({
    queryKey: ['freight-summary', summaryParams],
    enabled: !isMerchant,
    queryFn: async () => (await api.get<FreightSummary>('/freight/summary', { params: summaryParams })).data,
  });

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-freight'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchantList = useMemo(() => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []), [merchants]);

  const { data: couriers } = useQuery({
    queryKey: ['couriers-light-freight'],
    queryFn: async () => (await api.get<any[]>('/couriers')).data,
  });
  const courierList = useMemo(() => (Array.isArray(couriers) ? couriers : []), [couriers]);

  const moveToB2b = useMutation({
    mutationFn: (ids: number[]) => api.post('/freight/move-to-b2b', { ids }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['freight'] }); setSelected({}); },
  });

  const selectedIds = Object.keys(selected).filter((k) => selected[Number(k)]).map(Number);

  const handleExport = async () => {
    const qs = new URLSearchParams();
    qs.set('orderType', orderType);
    // The export mirrors exactly what's on screen — including the search term, which it used to
    // drop (so exporting a searched view silently returned the whole month).
    if (awbSearch) qs.set('q', awbSearch);
    if (legType) qs.set('legType', legType);
    if (merchantIds.length) qs.set('merchantIds', merchantIds.join(','));
    if (courierIds.length) qs.set('courierIds', courierIds.join(','));
    if (!searching) {
      if (dateFrom) qs.set('fromDate', dateFrom);
      if (dateTo) qs.set('toDate', dateTo);
    }
    await downloadBlob(`/freight/export?${qs.toString()}`, `freight-${orderType}.csv`);
  };

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Freight {orderType}</h1>
        <div className="flex gap-2">
          {/* Merchants get a read-only freight view — only Export. Re-Calculation / Upload / Move-to-B2B
              are internal-ops actions and are hidden for merchant logins. */}
          {!isMerchant && (
            <>
              <button onClick={() => setShowRecalc(true)} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1">
                <RotateCw size={14} /> Freight Re-Calculation
              </button>
              <button onClick={() => setShowUpload(true)} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1">
                <Upload size={14} /> Upload Freight
              </button>
              <button
                disabled={selectedIds.length === 0 || orderType === 'B2B'}
                onClick={() => moveToB2b.mutate(selectedIds)}
                className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1 disabled:opacity-50"
              >
                <ArrowRightLeft size={14} /> Move to B2B
              </button>
            </>
          )}
          <button onClick={handleExport} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1">
            <Download size={14} /> Export Freight[s]
          </button>
        </div>
      </div>
      {/* Charged / GST summary — internal staff only (merchants never see platform totals).
          Today and Yesterday ignore the date filter on purpose so they always answer "what did we
          charge today?"; the last tile follows whatever Freight Date range is selected. */}
      {!isMerchant && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-2">
            <SummaryTile label={`Today · ${orderType}`} b={summary?.today} accent />
            <SummaryTile label="Yesterday" b={summary?.yesterday} />
            <SummaryTile label="This Month" b={summary?.thisMonth} />
            <SummaryTile
              label="Selected Range"
              sub={summary?.filtered?.from || summary?.filtered?.to
                ? `${summary?.filtered?.from ?? '…'} → ${summary?.filtered?.to ?? '…'}`
                : 'All dates'}
              b={summary?.filtered} />
          </div>
          <div className="text-[11px] text-slate-400 mb-4">
            Amounts are freight charged to merchants, dated by when the charge was raised. Total includes {summary?.gstPct ?? 18}% GST.
          </div>
        </>
      )}

      {/* Filters */}
      <div className="grid grid-cols-12 gap-3 mb-4">
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Order No. / {orderType === 'B2B' ? 'LR No.' : 'AWB No.'}</label>
          <div className="relative">
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder={orderType === 'B2B' ? 'Order No, LR or AWB' : 'Order No or AWB No'}
              className="w-full border rounded px-2 py-1 pr-6 text-sm" />
            {searchInput && (
              <button type="button" onClick={() => setSearchInput('')} title="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                <X size={13} />
              </button>
            )}
          </div>
          {searching && <div className="text-[10px] text-amber-600 mt-0.5">Searching all dates — the Freight Date range is ignored</div>}
        </div>
        {!isMerchant && (
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Select Merchants</label>
          <MultiSelectFilter label="" placeholder="Select Merchants" values={merchantIds}
            onChange={(vals) => setMerchantIds(vals.map(Number))}
            options={merchantList.map((m: any) => ({ value: m.id, label: m.companyName + ' (#' + m.id + ')' }))} />
        </div>
        )}
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Select Couriers</label>
          <MultiSelectFilter label="" placeholder="Select Couriers" values={courierIds}
            onChange={(vals) => setCourierIds(vals.map(Number))}
            options={courierList.map((c: any) => ({ value: c.id, label: c.name + ' (#' + c.id + ')' }))} />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Freight Date</label>
          <DateRangePicker value={{ startDate: dateFrom, endDate: dateTo }} defaultPreset={datePreset} size="sm"
            onChange={(r) => { setDateFrom(r.startDate); setDateTo(r.endDate); setDatePreset(r.preset); setPage(1); }} />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Order Booking Date</label>
          <DateRangePicker value={{ startDate: bookingFrom, endDate: bookingTo }} defaultPreset={bookingPreset} size="sm"
            onChange={(r) => { setBookingFrom(r.startDate); setBookingTo(r.endDate); setBookingPreset(r.preset); setPage(1); }} />
        </div>
        {/* A returned parcel is billed twice against the same AWB — the forward move, then the
            return (whose COD charge is a credit). This picks which of the two you're looking at. */}
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Charge Type</label>
          <select value={legType} onChange={(e) => { setLegType(e.target.value as any); setPage(1); }}
            className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All charges</option>
            <option value="FORWARD">Forward only</option>
            <option value="RTO">RTO only (return + COD reversal)</option>
            <option value="WEIGHT">Weight charges only</option>
          </select>
        </div>
      </div>
      <div className="text-xs mb-2 text-gray-500">
        {selectedIds.length} Freights Selected • Showing {items.length} of {total}
        <span className="ml-2 text-slate-400">
          One row per charge — a returned shipment shows its AWB twice: <b className="text-slate-500">Forward</b>, then <b className="text-amber-600">RTO</b> with the COD charge reversed.
          A <b className="text-violet-600">Weight</b> row is the difference charged when the courier reported a heavier parcel after the freight had already been invoiced.
        </span>
      </div>
      <div className="overflow-x-auto bg-white border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="px-2 py-2">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    const c = e.target.checked;
                    const s: any = {};
                    if (c) items.forEach((i) => { s[i.id] = true; });
                    setSelected(s);
                  }}
                />
              </th>
              <th className="px-2 py-2 text-left">Charge</th>
              {!isMerchant && <th className="px-2 py-2 text-left">Merchant</th>}
              <th className="px-2 py-2 text-left">Courier</th>
              <th className="px-2 py-2 text-left">Order Number</th>
              <th className="px-2 py-2 text-left">{orderType === 'B2B' ? 'LR Number' : 'AWB Number'}</th>
              <th className="px-2 py-2 text-right">Charged Weight</th>
              <th className="px-2 py-2 text-left">Zone</th>
              <th className="px-2 py-2 text-right">COD Charge</th>
              <th className="px-2 py-2 text-right">Subtotal</th>
              <th className="px-2 py-2 text-right">Forward Charge</th>
              <th className="px-2 py-2 text-right">RTO Charge</th>
              <th className="px-2 py-2 text-right">GST</th>
              <th className="px-2 py-2 text-right">Total</th>
              <th className="px-2 py-2 text-left">Created At</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isMerchant ? 14 : 15} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={isMerchant ? 14 : 15} className="text-center py-6 text-gray-400">No freight records yet</td></tr>
            ) : (
              items.map((r) => (
                <tr key={r.id} className={`border-t hover:bg-gray-50 cursor-pointer ${r.legType === 'RTO' ? 'bg-amber-50/60' : isWeightLeg(r.legType) ? 'bg-violet-50/60' : ''}`} onClick={() => setDetailId(r.id)}>
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected({ ...selected, [r.id]: e.target.checked })} />
                  </td>
                  <td className="px-2 py-2">
                    <span
                      title={r.legType === 'RTO'
                        ? 'Return charge raised against the same AWB — the COD charge is reversed here because no COD is collected on an RTO'
                        : isWeightLeg(r.legType)
                          ? `Weight charge — the courier reported a heavier parcel AFTER this shipment's freight had already been invoiced. The invoiced row is left untouched and only the difference is charged here${r.bookedWeight ? ` (${r.bookedWeight} → ${r.chargedWeight} kg)` : ''}.`
                          : 'Forward movement — freight, plus the COD charge on a COD shipment'}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                        r.legType === 'RTO' ? 'bg-amber-100 text-amber-800'
                          : isWeightLeg(r.legType) ? 'bg-violet-100 text-violet-800'
                          : 'bg-emerald-100 text-emerald-800'}`}>
                      {r.legType === 'RTO' ? 'RTO' : isWeightLeg(r.legType) ? 'Weight' : 'Forward'}
                    </span>
                  </td>
                  {!isMerchant && (
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-emerald-700 text-white flex items-center justify-center text-xs">
                        {r.merchant?.companyName?.[0] ?? '?'}
                      </div>
                      <div className="text-xs">{r.merchant?.companyName ?? '—'}</div>
                    </div>
                  </td>
                  )}
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <Package size={14} />
                      </div>
                      <div className="text-xs">{r.courier?.addressLabelName ?? r.courier?.name ?? '—'}</div>
                    </div>
                  </td>
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <button type="button" title="View order details"
                      onClick={() => r.orderId && setOrderModalId(r.orderId)}
                      className="text-blue-700 underline hover:text-blue-900">{r.orderNo}</button>
                  </td>
                  <td className="px-2 py-2 font-mono text-xs" title="View freight breakdown">{orderType === 'B2B' ? r.lrNo : r.awbNo}</td>
                  <td className="px-2 py-2 text-right">{r.chargedWeight?.toFixed(2)}</td>
                  <td className="px-2 py-2">{r.zone}</td>
                  {/* A negative COD charge is the reversal on the return leg, not a data error —
                      call it out rather than letting it read as a normal amount. */}
                  <td className={`px-2 py-2 text-right ${r.codCharge < 0 ? 'text-amber-700 font-medium' : ''}`}
                      title={r.codCharge < 0 ? 'COD charge reversed — no COD is collected on a returned parcel' : undefined}>
                    {r.codCharge.toFixed(2)}
                  </td>
                  <td className={`px-2 py-2 text-right ${r.subtotal < 0 ? 'text-amber-700' : ''}`}>{r.subtotal.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right">{r.forwardCharge.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right">{r.rtoCharge.toFixed(2)}</td>
                  <td className={`px-2 py-2 text-right ${r.gst < 0 ? 'text-amber-700' : ''}`}>{r.gst.toFixed(2)}</td>
                  <td className={`px-2 py-2 text-right font-semibold ${r.total < 0 ? 'text-amber-700' : ''}`}>{amt0(r.total)}</td>
                  <td className="px-2 py-2 text-xs">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'NA'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-sm">
        <div>Rows per page: 50</div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-30">{'<'}</button>
          <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
          <button disabled={page >= lastPage} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-30">{'>'}</button>
        </div>
      </div>
      {showRecalc && (
        <RecalcModal
          orderType={orderType}
          merchants={merchantList}
          couriers={courierList}
          onClose={() => setShowRecalc(false)}
          onDone={() => { setShowRecalc(false); qc.invalidateQueries({ queryKey: ['freight'] }); }}
        />
      )}
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); qc.invalidateQueries({ queryKey: ['freight'] }); }}
        />
      )}
      {detailId !== null && <FreightDetailDrawer id={detailId} orderType={orderType} onClose={() => setDetailId(null)} />}
      {orderModalId && <OrderDetailsModal id={orderModalId} onClose={() => setOrderModalId(null)} />}
    </div>
  );
}

function RecalcModal({ orderType, merchants, couriers, onClose, onDone }: any) {
  const [selMerchants, setSelMerchants] = useState<number[]>([]);
  const [selCouriers, setSelCouriers] = useState<number[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const submit = useMutation({
    mutationFn: async () =>
      (
        await api.post('/freight/recalculate', {
          merchantIds: selMerchants,
          courierIds: selCouriers,
          fromDate: from,
          toDate: to,
          orderType,
        })
      ).data,
    onSuccess: (d: any) => {
      alert(`Re-calculated ${d.processed} shipments (${d.inserted} new, ${d.updated} updated)`);
      onDone();
    },
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[820px]">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between rounded-t">
          <div className="font-semibold">Freight Re Calculate</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Merchant(s)</label>
            <MultiSelectFilter label="" placeholder="Search & select merchants…" values={selMerchants}
              onChange={(vals) => setSelMerchants(vals.map(Number))}
              options={merchants.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}${m.brandName ? ` (${m.brandName})` : ''}` }))} />
            <div className="text-xs text-gray-400 mt-1">Search by name/ID; leave empty for all merchants.</div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Courier(s)</label>
            <MultiSelectFilter label="" placeholder="Search & select couriers…" values={selCouriers}
              onChange={(vals) => setSelCouriers(vals.map(Number))}
              options={couriers.map((c: any) => ({ value: c.id, label: `#${c.id} · ${c.name}` }))} />
            <div className="text-xs text-gray-400 mt-1">Search by name/ID; leave empty for all couriers.</div>
          </div>
          <div>
            <label className="block text-xs text-gray-500">Select Date Range</label>
            <div className="flex gap-1">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="flex-1 border rounded px-2 py-1.5 text-sm" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="flex-1 border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div className="flex items-center pt-5">
            <div className="text-xs text-gray-500">Forward vs RTO freight is applied automatically per each shipment's status — delivered / in-transit charge forward only; RTO statuses add the return leg.</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => submit.mutate()} disabled={submit.isPending} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
            {submit.isPending ? 'Calculating…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ORDER_STATUS_LABEL: Record<number, string> = {
  1: 'Booking Received', 2: 'Pickup Scheduled', 3: 'Picked', 4: 'At Branch', 5: 'In Transit',
  6: 'Arrived at Dest', 7: 'Out for Delivery', 8: 'Delivered', 9: 'Delivery Failed',
  10: 'RTO In Transit', 11: 'RTO At Branch', 12: 'RTO Delivered', 13: 'Lost', 14: 'RTO Lost',
  15: 'Forward Damage', 16: 'RTO Damage', 17: 'Held Up', 18: 'RTO Pending', 19: 'Partially Delivered',
  20: 'Booking Pending', 21: 'Booking Failed', 23: 'Cancelled', 24: 'Not Shipped', 33: 'Auto Cancelled',
};

function OrderDetailsModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: o, isLoading, isError } = useQuery({
    queryKey: ['order', id],
    queryFn: async () => (await api.get<any>(`/orders/${id}`)).data,
  });

  const Row = ({ label, value }: { label: string; value: any }) => (
    <div className="flex gap-2 text-sm py-0.5">
      <span className="text-slate-500 w-36 shrink-0">{label}</span>
      <span className="text-slate-800 break-words">{value ?? '—'}</span>
    </div>
  );
  const Section = ({ title, children }: { title: string; children: any }) => (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-xs font-semibold uppercase text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
  const money = (v: any) => (v != null && v !== '' ? `₹${Number(v).toLocaleString('en-IN')}` : '—');

  return (
    <Modal isOpen onClose={onClose} title={o ? `Order ${o.orderNo}` : 'Order details'}
      subtitle={o ? `${o.orderType} · ${o.deliveryType}` : undefined} maxWidth="4xl">
      {isLoading ? (
        <div className="py-12 text-center text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></div>
      ) : isError || !o ? (
        <div className="py-12 text-center text-rose-500 text-sm">Could not load this order.</div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">{ORDER_STATUS_LABEL[o.statusId] ?? `#${o.statusId}`}</span>
            {o.awbNo && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 font-mono">AWB {o.awbNo}</span>}
            {o.lrNo && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 font-mono">LR {o.lrNo}</span>}
            <a href={`/${o.orderType === 'B2B' ? 'orders-b2b' : 'orders-b2c'}/${o.id}`} target="_blank" rel="noreferrer"
              className="ml-auto text-xs text-primary underline">Open full order ↗</a>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <Section title="Customer">
              <Row label="Name" value={o.customerName} />
              <Row label="Mobile" value={o.customerMobile} />
              <Row label="Address" value={[o.customerAddress, o.customerCity, o.customerState, o.customerPincode].filter(Boolean).join(', ')} />
              {o.customerLandmark && <Row label="Landmark" value={o.customerLandmark} />}
            </Section>
            <Section title="Shipment">
              <Row label="Merchant" value={o.merchant?.companyName ?? (o.merchantId ? `#${o.merchantId}` : '—')} />
              <Row label="Courier" value={o.courier?.name ?? (o.courierId ? `#${o.courierId}` : '—')} />
              <Row label="Charged Wt" value={o.chargeableWeight ? `${o.chargeableWeight} kg` : '—'} />
              <Row label="Captured Wt" value={o.capturedWeight ? `${o.capturedWeight} kg` : '—'} />
              <Row label="Boxes" value={o.totalBoxes ?? '—'} />
            </Section>
            <Section title="Pickup">
              <Row label="Address" value={[o.pickupAddress, o.pickupPincode].filter(Boolean).join(', ')} />
              {o.pickupPersonName && <Row label="Contact" value={`${o.pickupPersonName}${o.pickupPersonPhone ? ' · ' + o.pickupPersonPhone : ''}`} />}
            </Section>
            <Section title="Financials & Dates">
              <Row label="Payment" value={o.deliveryType} />
              <Row label="Invoice Value" value={money(o.invoiceValue)} />
              {o.deliveryType === 'COD' && <Row label="COD Value" value={money(o.codValue)} />}
              <Row label="Booked" value={o.bookingDate ? new Date(o.bookingDate).toLocaleString() : '—'} />
              <Row label="Last Update" value={o.lastEventDate ? new Date(o.lastEventDate).toLocaleString() : '—'} />
              {o.edd && <Row label="EDD" value={new Date(o.edd).toLocaleDateString()} />}
            </Section>
          </div>

          {Array.isArray(o.products) && o.products.length > 0 && (
            <Section title={`Products (${o.products.length})`}>
              <div className="max-h-40 overflow-auto">
                {o.products.map((p: any, i: number) => (
                  <div key={i} className="flex justify-between text-sm py-0.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-700 truncate">{p.name ?? p.productName ?? p.description ?? `Item ${i + 1}`}</span>
                    <span className="text-slate-500 shrink-0 ml-2">Qty {p.quantity ?? p.qty ?? 1}{p.value ? ` · ${money(p.value)}` : ''}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
}

function UploadModal({ onClose, onDone }: any) {
  const [text, setText] = useState('');
  const submit = useMutation({
    mutationFn: async () => {
      const lines = text.trim().split(/\r?\n/);
      const header = lines.shift()?.split(',').map((h) => h.trim()) ?? [];
      const rows = lines.map((l) => {
        const cells = l.split(',');
        const r: any = {};
        header.forEach((h, i) => { r[h] = cells[i]?.trim(); });
        return r;
      });
      return (await api.post('/freight/upload', { rows })).data;
    },
    onSuccess: (d: any) => { alert(`Uploaded ${d.updated} freight overrides`); onDone(); },
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[640px]">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between rounded-t">
          <div className="font-semibold">Upload Freight</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="text-xs text-gray-500 mb-2">CSV format: <code>awbNo,subtotal,gst,total,forwardBase,forwardAdditional,rtoBase,rtoAdditional,codCharge,fuelSurcharge,weightDispute</code></div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className="w-full border rounded px-2 py-1.5 text-xs font-mono" placeholder="awbNo,subtotal,gst,total..." />
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => submit.mutate()} disabled={submit.isPending || !text} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
            {submit.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FreightDetailDrawer({ id, orderType, onClose }: { id: number; orderType: 'B2C' | 'B2B'; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['freight-detail', id],
    queryFn: async () => (await api.get(`/freight/${id}`)).data as any,
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}>
      <div className="absolute inset-x-6 top-12 bg-white rounded-lg shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b pb-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-50 text-emerald-700 rounded flex items-center justify-center"><FileText size={18} /></div>
            <h2 className="text-lg font-semibold">Freight Details {orderType}</h2>
            {data?.legType && (
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                data.legType === 'RTO' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                {data.legType === 'RTO' ? 'RTO charge' : 'Forward charge'}
              </span>
            )}
          </div>
          <div className="flex gap-6 text-xs">
            <div><div className="text-gray-500">TOTAL WEIGHT CHARGED:</div><div className="font-semibold">{data?.totalWeightCharged?.toFixed(2) ?? '—'}</div></div>
            <div><div className="text-gray-500">SUBTOTAL:</div><div className="font-semibold">{data?.subtotal?.toFixed(2) ?? '—'}</div></div>
            <div><div className="text-gray-500">GST:</div><div className="font-semibold">{data?.gst?.toFixed(2) ?? '—'}</div></div>
            <div><div className="text-gray-500">TOTAL:</div><div className="font-semibold">{data?.total != null ? amt0(data.total) : '—'}</div></div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {isLoading ? (
          <div className="text-gray-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-8 text-sm">
            <div>
              <div className="text-emerald-700 font-semibold mb-3">Freight Description</div>
              {/* Every component that makes up the subtotal, and the subtotal underneath them.
                  The B2B rows — processing, ODA, ROV, green tax, POD, to-pay, FM pickup, handling —
                  were computed, stored and billed but never SHOWN, so a ₹452.50 subtotal sat under a
                  breakdown reading 350.00 + 0.00 + 52.50 and ₹50 of it was unexplained. Anything that
                  is zero stays hidden; anything that was charged has to appear. */}
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div><div className="text-gray-500 uppercase">Zone:</div><div>{data?.zone || '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Shipment Status:</div><div>{data?.shipmentStatus ?? '—'}</div></div>
                {data?.orderType === 'B2B' ? (
                  <>
                    <div>
                      <div className="text-gray-500 uppercase">Rate:</div>
                      <div>
                        {data?.ratePerKg ? `₹${Number(data.ratePerKg).toFixed(2)}/kg` : '—'}
                        {data?.rateSource && <span className="text-gray-400"> · by {data.rateSource}</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 uppercase">Freight (rate × weight):</div>
                      <div>
                        {data?.rateOnWeight != null ? Number(data.rateOnWeight).toFixed(2) : '—'}
                        {/* Why the base charge can be higher than rate × weight — the one question
                            this screen is opened to answer on a light consignment. */}
                        {data?.minChargeApplied && (
                          <span className="text-amber-700"> → minimum LR charge applied</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div><div className="text-gray-500 uppercase">Additional Weight Applied:</div><div>{Number(data?.additionalWeightApplied ?? 0).toFixed(2)}</div></div>
                )}
                <div><div className="text-gray-500 uppercase">Forward Base Charge:</div><div>{Number(data?.forwardBaseCharge ?? 0).toFixed(2)}</div></div>
                <div><div className="text-gray-500 uppercase">Additional Forward Freight Charges:</div><div>{Number(data?.additionalForwardFreightCharges ?? 0).toFixed(2)}</div></div>
                <div><div className="text-gray-500 uppercase">COD Charges:</div><div>{Number(data?.codCharges ?? 0).toFixed(2)}</div></div>
                <div><div className="text-gray-500 uppercase">Additional RTO Freight Charges:</div><div>{Number(data?.additionalRtoFreightCharges ?? 0).toFixed(2)}</div></div>
                <div><div className="text-gray-500 uppercase">RTO Charges:</div><div>{Number(data?.rtoCharges ?? 0).toFixed(2)}</div></div>
                <div><div className="text-gray-500 uppercase">Fuel Surcharge:</div><div>{Number(data?.fuelSurcharge ?? 0).toFixed(2)}</div></div>
                {([
                  ['Processing Charge', data?.processingCharge],
                  ['ODA Charge', data?.odaCharge],
                  ['ROV / Insurance', data?.insuranceCharge],
                  ['Handling Charge', data?.handlingCharge],
                  ['Appointment Charge', data?.appointmentCharge],
                  ['State Charge', data?.stateCharge],
                  ['Green Tax', data?.greenTax],
                  ['POD Charge', data?.podCharge],
                  ['To-Pay Handling', data?.toPayCharges],
                  ['FM Pickup Charge', data?.fmCharges],
                ] as [string, any][])
                  .filter(([, v]) => Math.abs(Number(v ?? 0)) > 0.004)
                  .map(([label, v]) => (
                    <div key={label}><div className="text-gray-500 uppercase">{label}:</div><div>{Number(v).toFixed(2)}</div></div>
                  ))}
              </div>
              <div className="mt-3 pt-2 border-t flex justify-between text-xs font-semibold">
                <span className="text-gray-500 uppercase">Subtotal (sum of the above)</span>
                <span>{Number(data?.subtotal ?? 0).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <div className="text-emerald-700 font-semibold mb-3">Order Description</div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div><div className="text-gray-500 uppercase">Order Date:</div><div>{data?.orderDate ? new Date(data.orderDate).toLocaleDateString() : '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Booking State:</div><div>{data?.bookingState ?? '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Charged On:</div><div>{data?.chargedOn ? new Date(data.chargedOn).toLocaleDateString() : 'NA'}</div></div>
                <div><div className="text-gray-500 uppercase">Destination Pincode:</div><div>{data?.destinationPincode ?? '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Booking Pincode:</div><div>{data?.bookingPincode ?? '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Destination City:</div><div>{data?.destinationCity ?? '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Booking City:</div><div>{data?.bookingCity ?? '—'}</div></div>
                <div><div className="text-gray-500 uppercase">Destination State:</div><div>{data?.destinationState ?? '—'}</div></div>
              </div>
            </div>
            {/* What this parcel has cost in total. A returned shipment is billed twice against one
                AWB, so the row you clicked is only half the story. */}
            {(data?.shipmentLegs?.length ?? 0) > 1 && (
              <div className="col-span-2 border-t pt-4">
                <div className="text-emerald-700 font-semibold mb-2">This shipment — all charges on {data?.awbNo ?? data?.lrNo ?? 'this AWB'}</div>
                <table className="text-xs w-full max-w-2xl">
                  <thead className="text-gray-500 uppercase">
                    <tr>
                      <th className="text-left py-1">Charge</th>
                      <th className="text-right py-1">COD</th>
                      <th className="text-right py-1">Subtotal</th>
                      <th className="text-right py-1">GST</th>
                      <th className="text-right py-1">Total</th>
                      <th className="text-left py-1 pl-4">Raised</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.shipmentLegs.map((l: any) => (
                      <tr key={l.id} className={`border-t ${l.id === data.id ? 'font-semibold' : ''}`}>
                        <td className="py-1">{l.legType === 'RTO' ? 'RTO (return)' : 'Forward'}</td>
                        <td className={`text-right py-1 ${l.codCharge < 0 ? 'text-amber-700' : ''}`}>{Number(l.codCharge).toFixed(2)}</td>
                        <td className="text-right py-1">{Number(l.subtotal).toFixed(2)}</td>
                        <td className="text-right py-1">{Number(l.gst).toFixed(2)}</td>
                        <td className="text-right py-1">{amt0(l.total)}</td>
                        <td className="py-1 pl-4 text-gray-500">{l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 font-semibold">
                      <td className="py-1">Net billed</td>
                      <td className="text-right py-1">{Number(data.shipmentCod ?? 0).toFixed(2)}</td>
                      <td className="text-right py-1">{Number(data.shipmentSubtotal ?? 0).toFixed(2)}</td>
                      <td className="text-right py-1">{Number(data.shipmentGst ?? 0).toFixed(2)}</td>
                      <td className="text-right py-1">{amt0(data.shipmentTotal)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
                <div className="text-[11px] text-gray-400 mt-1">
                  Net COD is 0.00 on a returned parcel — the forward COD charge is reversed on the RTO row.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function FreightB2cPage() {
  return <FreightPage orderType="B2C" />;
}
export function FreightB2bPage() {
  return <FreightPage orderType="B2B" />;
}
